import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

const LAST_UPDATED = '2026-05-26';

/**
 * Refund Policy page.
 *
 * EU consumer law gives buyers of digital content a 14-day right of withdrawal (Directive
 * 2011/83/EU, Art. 16(m)). That right is automatically waived for digital content delivered
 * immediately ONLY IF the consumer gives express prior consent AND acknowledges they lose
 * the right of withdrawal by starting the download. We collect that acknowledgement implicitly
 * when the user clicks "Run scan" (the report is delivered immediately after).
 *
 * We then offer a 7-day money-back as a goodwill policy ON TOP OF the statutory withdrawal,
 * which positions us competitively without weakening our legal stance. This page documents
 * both layers explicitly so Stripe / PayPal disputes against us have a definitive reference.
 */
export default function RefundPolicyPage() {
  useSeo({
    title: 'Refund policy',
    canonicalPath: '/refund-policy',
    description:
      'EAA Proof refund policy: 7-day money-back guarantee, EU consumer right of withdrawal, eligibility rules, and how to request a refund.',
  });

  return (
    <div className="page legal-page">
      <header>
        <h1>Refund policy</h1>
        <p className="muted">Last updated: {LAST_UPDATED}</p>
      </header>

      <section>
        <h2>Summary</h2>
        <ul>
          <li>
            <strong>7-day money-back guarantee</strong> on every purchase — no questions asked
            during the first 7 days after payment.
          </li>
          <li>
            <strong>EU 14-day right of withdrawal</strong> for digital content applies until you
            start your first scan. By clicking <em>"Run scan"</em> you expressly consent to
            immediate performance and acknowledge that you lose the statutory withdrawal right
            for that scan.
          </li>
          <li>
            <strong>Subscriptions:</strong> cancel anytime via the Customer Portal. You keep
            access until the end of the paid period.
          </li>
          <li>
            <strong>Process:</strong> email{' '}
            <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> with your order id.
            Refunds are issued back to the original payment method within 5 business days.
          </li>
        </ul>
      </section>

      <section>
        <h2>1. Statutory right of withdrawal (EU consumers)</h2>
        <p>
          Under Article 9 of EU Directive 2011/83/EU on consumer rights, consumers in the EU
          have a 14-day right to withdraw from a distance contract without giving reasons. For
          digital content not supplied on a tangible medium (e.g. our scan report and
          accessibility statement HTML), the right of withdrawal lapses early if{' '}
          <strong>both</strong> of the following are true (Article 16(m)):
        </p>
        <ol>
          <li>
            You gave express prior consent to the performance starting before the end of the
            14-day period; and
          </li>
          <li>
            You acknowledged that you would lose your right of withdrawal upon performance.
          </li>
        </ol>
        <p>
          <strong>How we obtain these:</strong> when you click <em>"Run scan"</em> in the app,
          we treat that click as your express consent and acknowledgement. A scan completes
          within 60–120 seconds — the digital content (PDF + HTML) is delivered immediately.
          From that moment, the statutory withdrawal right for that specific scan is waived.
        </p>
        <p>
          If you have purchased a plan but have <strong>not yet run any scan</strong>, you
          retain the full statutory 14-day withdrawal right and can request a full refund by
          emailing <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
        </p>
      </section>

      <section>
        <h2>2. Our 7-day goodwill refund (in addition to the above)</h2>
        <p>
          We offer a 7-day money-back guarantee that goes <strong>beyond</strong> the statutory
          minimum. Within 7 days of your purchase you may request a full refund for any reason —
          even if you have already run scans — provided that:
        </p>
        <ul>
          <li>You have not downloaded the resulting PDF report more than 5 times; and</li>
          <li>
            You have not republished the generated accessibility statement HTML on a live site
            crawlable by search engines.
          </li>
        </ul>
        <p>
          These limits protect against the obvious abuse pattern (download the report, get a
          refund, keep the deliverable). For genuine "this wasn't what I needed" requests, the
          limits never bite.
        </p>
      </section>

      <section>
        <h2>3. Subscription cancellations</h2>
        <p>
          You can cancel your Basic or Agency subscription at any time from{' '}
          <Link to="/profile">your profile</Link> → "Manage subscription" → Cancel. Cancelling
          stops future renewals; you retain access to scan credits and reports until the end of
          the current billing period.
        </p>
        <p>
          We do not pro-rate refunds for unused time on monthly plans. If a renewal charges you
          unexpectedly (e.g. you forgot to cancel), email{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> within 5 days of the
          charge and we will refund the renewal in full as long as you haven't used scans from
          that new billing cycle.
        </p>
      </section>

      <section>
        <h2>4. Failed scans and service issues</h2>
        <p>
          If a scan fails for reasons attributable to us (our scanner crashes, Cloudflare R2 is
          down, the AI remediation service times out), the credit is automatically not debited
          and you can immediately re-attempt. If you've experienced a pattern of failures or a
          scan completed but the report is unusable (e.g. blank PDF, broken HTML), email{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> with your scan id and
          we'll either credit your account or process a refund.
        </p>
      </section>

      <section>
        <h2>5. How to request a refund</h2>
        <ol>
          <li>
            Email <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> with the
            subject line "<em>Refund request — &lt;order id&gt;</em>".
          </li>
          <li>
            Include: the email address on your account, the order id (from your Stripe receipt
            or our profile page), and the reason for the refund (helps us improve the product —
            not required).
          </li>
          <li>
            We acknowledge within 1 business day. Approved refunds are issued back to the
            original payment method within 5 business days. Your bank may take an additional 1-7
            business days to post the credit.
          </li>
        </ol>
      </section>

      <section>
        <h2>6. Chargebacks and disputes</h2>
        <p>
          Please contact us first before filing a chargeback with your bank or card issuer.
          Most refund-eligible cases are resolved in under 24 hours by email. Chargebacks filed
          without prior contact will be contested with Stripe using this policy and the
          timestamped scan logs.
        </p>
      </section>

      <section>
        <h2>7. EU online dispute resolution</h2>
        <p>
          For unresolved disputes, EU consumers can use the European Commission's Online
          Dispute Resolution platform at{' '}
          <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
            ec.europa.eu/consumers/odr
          </a>
          . We are not obliged and not willing to participate in dispute resolution proceedings
          before a consumer arbitration board.
        </p>
      </section>

      <section className="legal-related">
        <h2>Related</h2>
        <p>
          <Link to="/terms">Terms of service</Link> · <Link to="/privacy">Privacy policy</Link>
          {/* · <Link to="/imprint">Imprint</Link> — re-enable when imprint page is filled in */}
        </p>
      </section>
    </div>
  );
}
